import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import SidebarLayout from "./layouts/SidebarLayout";
import { GlobalColor, GlobalSecondaryColor } from "./global/GlobalColor";

import Dashboard from "./pages/Dashboard";
import CategoriesPage from "./pages/CategoriesPage";
import OrdersPage from "./pages/OrdersPage";
import BillingPage from "./pages/BillingPage";
import ProductsPage from "./pages/ProductsPage";
import OtherProductsPage from "./pages/OtherProductsPage";
import DrinksPage from "./pages/DrinksPage";
import RestaurantsPage from "./pages/RestaurantsPage";
import CarouselsPage from "./pages/CarouselPage";
import UserPage from "./pages/userPage";
import DiscountsPage from "./pages/DiscountsPage";
import DeliveryPage from "./pages/DeliveryPage";
import { AuthProvider } from "./auth/AuthProvider";
import { LoginPage } from "./pages/LoginPage";
import SettingsPage from "./pages/SettingsPage";
import AdditionsPage from "./pages/AdditionsPage";
// import CreditCustomersPage from "./pages/CreditCustomersPage";
import { ProtectedRoute } from "./components/ProtectedRoute";
import CashClosingPage from "./pages/CashClosingPage";
import CreateOrderPage from "./pages/CreateOrderPage";
import InventoryPage from "./pages/InventoryPage";
import FlavorsPage from "./pages/FlavorsPage";
import WorkersPage from "./pages/WorkersPage";
import PromotionsPage from "./pages/PromotionsPage";
import WorkerMetricsPage from "./pages/WorkerMetricsPage";
import CustomerMetricsPage from "./pages/CustomerMetricsPage";
import ConfigPage from "./pages/ConfigPage";
import ReservationsPage from "./pages/ReservationsPage";
import TutorialPage from "./pages/TutorialPage";
import DistripakoCategoriasPage from "./pages/DistripakoCategoriasPage";
import DistripakoProductosPage from "./pages/DistripakoProductosPage";
import DistripakoAliadosPage from "./pages/DistripakoAliadosPage";
import DistripakoCotizacionesPage from "./pages/DistripakoCotizacionesPage";
import DistripakoCitasPage from "./pages/DistripakoCitasPage";
import DistripakoCreditosPage from "./pages/DistripakoCreditosPage";
import DistripakoBankFormsPage from "./pages/DistripakoBankFormsPage";
import DistripakoUsuariosPage from "./pages/DistripakoUsuariosPage";


const theme = createTheme({
  palette: {
    primary: {
      main: GlobalColor,
      light: "#1e293b",
      dark: "#020617",
      contrastText: "#ffffff",
    },
    secondary: {
      main: GlobalSecondaryColor,
      light: "#64748b",
      dark: "#334155",
      contrastText: "#ffffff",
    },
    success: {
      main: "#10b981",
      light: "#34d399",
      dark: "#059669",
    },
    background: {
      default: "#f8fafc",
      paper: "#ffffff",
    },
    text: {
      primary: "#0f172a",
      secondary: "#64748b",
    },
    divider: "#e2e8f0",
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: "none",
          fontWeight: 700,
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.03), 0 1px 2px -1px rgba(0, 0, 0, 0.03)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid #f1f5f9",
        },
      },
    },
  },
});

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <BrowserRouter>
        <AuthProvider>
          <CssBaseline />
          <Routes>
            <Route path="login" element={<LoginPage />} />

            {/* Rutas Protegidas - Solo usuarios logueados */}
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<RestaurantsPage />} />
              <Route path="settings" element={<SettingsPage />} />

              <Route path="/:restaurantId" element={<SidebarLayout />}>
                {/* Redirección por defecto al entrar al restaurante: al Dashboard estilo Jobgio */}
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Dashboard />} />

                {/* Rutas compartidas entre Admin y Cajero */}
                <Route element={<ProtectedRoute allowedRoles={["admin", "cajero"]} />}>
                  <Route path="billing" element={<BillingPage />} />
                  <Route path="cash-closing" element={<CashClosingPage />} />
                  <Route path="orders" element={<OrdersPage />} />
                  <Route path="create-order" element={<CreateOrderPage />} />
                  <Route path="edit-order/:orderId" element={<CreateOrderPage />} />
                  <Route path="reservations" element={<ReservationsPage />} />
                  {/* <Route path="credit-customers" element={<CreditCustomersPage />} /> */}

                </Route>

                {/* Rutas exclusivas para Admin */}
                <Route element={<ProtectedRoute allowedRoles={["admin"]} />}>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="products" element={<ProductsPage />} />
                  <Route path="other-products" element={<OtherProductsPage />} />
                  <Route path="categories" element={<CategoriesPage />} />
                  <Route path="carousel" element={<CarouselsPage />} />
                  <Route path="users" element={<UserPage />} />
                  <Route path="discounts" element={<DiscountsPage />} />
                  <Route path="delivery" element={<DeliveryPage />} />
                  <Route path="additions" element={<AdditionsPage />} />
                  <Route path="drinks" element={<DrinksPage />} />
                  <Route path="inventory" element={<InventoryPage />} />
                  <Route path="flavors" element={<FlavorsPage />} />
                  <Route path="workers" element={<WorkersPage />} />
                  <Route path="promotions" element={<PromotionsPage />} />
                  <Route path="worker-metrics" element={<WorkerMetricsPage />} />
                  <Route path="customer-metrics" element={<CustomerMetricsPage />} />
                  <Route path="config" element={<ConfigPage />} />
                  <Route path="tutorial" element={<TutorialPage />} />
                  {/* Rutas Distripako Web */}
                  <Route path="web-categories" element={<DistripakoCategoriasPage />} />
                  <Route path="web-products" element={<DistripakoProductosPage />} />
                  <Route path="web-allies" element={<DistripakoAliadosPage />} />
                  <Route path="web-quotes" element={<DistripakoCotizacionesPage />} />
                  <Route path="web-appointments" element={<DistripakoCitasPage />} />
                  <Route path="web-users" element={<DistripakoUsuariosPage />} />
                  <Route path="web-credits" element={<DistripakoCreditosPage />} />
                  <Route path="web-bank-forms" element={<DistripakoBankFormsPage />} />

                </Route>
              </Route>
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
